/**
 * Situation vector — a declared description of the room a decision will be made
 * in. Four optional answers, each a small enum, supplied by the person setting
 * up the session. It describes the process, never the people, and it is visible
 * to every participant.
 *
 * Declared first, observed second: corroborating signals (prior hidden-dissent
 * findings, open fairness concerns) may PROMPT an answer; they never write one.
 *
 * An absent vector, or an absent answer, means "not declared" and leaves the
 * recommendation path exactly as it was before this type existed.
 *
 * Source: docs/9-exploration/PRD-method-recommendation.md §5 Phase 2.
 */

/** Bump when the set of dimensions or their values changes shape. Stored beside the vector. */
export const SITUATION_VECTOR_VERSION = 1;

/** Is there someone whose view will set the frame before others speak? */
export type SituationVoice = 'balanced' | 'anchored';
/** Will people say what they actually think, on the record, in front of each other? */
export type SituationCandour = 'open' | 'guarded';
/** Has this group reversed a decision of this kind recently? */
export type SituationStability = 'stable' | 'reversing';
/** Do you agree on what matters and disagree on the items, or disagree on what matters? */
export type SituationAltitude = 'criteria_agreed' | 'criteria_contested';

/**
 * Keys are deliberately single words: the row is written through the generic
 * camel→snake column mapper, and a single-word key survives any conversion
 * unchanged.
 */
export interface SituationVector {
  voice?: SituationVoice;
  candour?: SituationCandour;
  stability?: SituationStability;
  /** Agreement altitude: do you disagree on the items, or on what matters? */
  altitude?: SituationAltitude;
}

export type SituationDimension = keyof SituationVector;

export const SITUATION_DIMENSIONS: readonly SituationDimension[] = [
  'voice',
  'candour',
  'stability',
  'altitude',
];

export interface SituationQuestionOption {
  value: string;
  /** Shown as the answer. */
  label: string;
}

export interface SituationQuestion {
  dimension: SituationDimension;
  /** Short name for the dimension, used as a heading. */
  title: string;
  /** The question as the facilitator is asked it. Shown verbatim. */
  question: string;
  options: readonly SituationQuestionOption[];
}

/**
 * The four questions, in the order they are asked. All optional, all skippable.
 * The skip path is today's behaviour exactly.
 */
export const SITUATION_QUESTIONS: readonly SituationQuestion[] = [
  {
    dimension: 'voice',
    title: 'Voice',
    question: 'Is there someone in this group whose view will set the frame before others speak?',
    options: [
      { value: 'balanced', label: 'No, voices are balanced' },
      { value: 'anchored', label: 'Yes, one voice anchors the room' },
    ],
  },
  {
    dimension: 'candour',
    // The stored key keeps its original spelling; the label is American.
    title: 'Candor',
    question: 'Will people say what they actually think, on the record, in front of each other?',
    options: [
      { value: 'open', label: 'Yes' },
      { value: 'guarded', label: 'Not reliably' },
    ],
  },
  {
    dimension: 'stability',
    title: 'Stability',
    question: 'Has this group reversed a decision of this kind recently?',
    options: [
      { value: 'stable', label: 'No' },
      { value: 'reversing', label: 'Yes' },
    ],
  },
  {
    dimension: 'altitude',
    title: 'Agreement altitude',
    question:
      'Do you agree on what matters and disagree on the items, or disagree on what matters?',
    options: [
      { value: 'criteria_agreed', label: 'We agree on what matters' },
      { value: 'criteria_contested', label: 'We disagree on what matters' },
    ],
  },
];

const ALLOWED_VALUES: Record<SituationDimension, ReadonlySet<string>> = SITUATION_QUESTIONS.reduce<
  Record<SituationDimension, ReadonlySet<string>>
>(
  (acc, q) => {
    acc[q.dimension] = new Set(q.options.map((o) => o.value));
    return acc;
  },
  { voice: new Set(), candour: new Set(), stability: new Set(), altitude: new Set() },
);

/** Label for a stored value, for display. Unknown values fall back to the raw value. */
export function situationValueLabel(dimension: SituationDimension, value: string | undefined): string {
  if (!value) return 'Not declared';
  const question = SITUATION_QUESTIONS.find((q) => q.dimension === dimension);
  return question?.options.find((o) => o.value === value)?.label ?? value;
}

/** True when nothing has been declared. */
export function isEmptySituationVector(vector: SituationVector | null | undefined): boolean {
  if (!vector) return true;
  return SITUATION_DIMENSIONS.every((d) => vector[d] === undefined);
}

/**
 * Parse an untrusted value (a jsonb column, a form payload) into a vector.
 * Unknown keys and invalid values are dropped rather than thrown; a value that
 * is not an object yields null. Never returns an empty object — an empty
 * vector is returned as null so "absent" has one spelling.
 */
export function parseSituationVector(raw: unknown): SituationVector | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const record = raw as Record<string, unknown>;
  const out: SituationVector = {};
  for (const dimension of SITUATION_DIMENSIONS) {
    const value = record[dimension];
    if (typeof value === 'string' && ALLOWED_VALUES[dimension].has(value)) {
      (out as Record<string, string>)[dimension] = value;
    }
  }
  return isEmptySituationVector(out) ? null : out;
}

export function situationVectorEquals(
  a: SituationVector | null | undefined,
  b: SituationVector | null | undefined,
): boolean {
  return SITUATION_DIMENSIONS.every((d) => (a?.[d] ?? undefined) === (b?.[d] ?? undefined));
}
